<template>
  <b-overlay class="h-100" :show="isLoading" rounded="lg">
    <div class="card h-100">
      <div class="card-header d-flex customStickyCardHeader">
        <button class="close-sidebar-btn" type="button" @click="onHideForm">
          <i class="icon-close f-14" />
        </button>

        <h6 class="card-header-title">
          {{ title }}
        </h6>

        <div v-if="isEditForm">
          <span>
            {{ $t("lbl_EMPBE_CreatedTime") }}: {{ form.created }}<br />
            {{ $t("lbl_EMPBE_UpdatedTime") }}: {{ form.updated }}
          </span>
        </div>
      </div>

      <div class="card-body pt-0 pb-4 overflow-auto">
        <div class="mt-4">
          <div v-if="isNewForm || isEditForm">
            <custom-collapse class="mb-4">
              <template #header>
                <h5 class="mb-0">
                  {{ $t("lbl_EMPBE_Instruction") }}
                </h5>
              </template>

              <div class="row mr-2">
                <div class="col-6">
                  <h6 class="font-weight-medium">
                    {{ $t("lbl_EMPBE_Reminder") }}
                  </h6>
                  <ol>
                    <li
                      v-for="(msg, index) in messages('detail_')"
                      :key="index"
                    >
                      {{ msg }}
                    </li>
                  </ol>
                </div>

                <div class="col-6">
                  <h6 class="font-weight-medium">
                    {{ $t("lbl_EMPBE_Example") }}
                  </h6>
                  <ol>
                    <li
                      v-for="(msg, index) in messages('example_')"
                      :key="index"
                    >
                      {{ msg }}
                    </li>
                  </ol>
                </div>
              </div>
            </custom-collapse>

            <ValidationObserver ref="observer">
              <div class="row">
                <div class="col-12 p-b-20">
                  <h6>
                    {{ this.$t("lbl_EMPBE_OpenSearch_Key") }}
                    <span class="text-danger">*</span>
                  </h6>

                  <b-form-input
                    v-model="form.key"
                    :state="getValidationState('key')"
                    class="CPSForm-input"
                    :disabled="isEditForm"
                    @input="formErrorMsg.key = null"
                  />

                  <b-form-invalid-feedback :state="getValidationState('key')">
                    {{ formErrorMsg.key }}
                  </b-form-invalid-feedback>
                </div>

                <div class="col-12">
                  <h6>
                    {{ this.$t("lbl_EMPBE_OpenSearch_Value") }}
                    <span class="text-danger">*</span>
                  </h6>

                  <b-form-input
                    v-model="form.value"
                    :state="getValidationState('value')"
                    class="CPSForm-input"
                    @input="formErrorMsg.value = null"
                  />

                  <b-form-invalid-feedback :state="getValidationState('value')">
                    {{ formErrorMsg.value }}
                  </b-form-invalid-feedback>
                </div>
              </div>
            </ValidationObserver>
          </div>

          <div v-else>
            <custom-collapse>
              <template #header>
                <h5 class="mb-0">
                  {{ $t("lbl_EMPBE_Instruction") }}
                </h5>
              </template>

              <div class="row mr-2">
                <div class="col-6">
                  <h6 class="font-weight-medium">
                    {{ $t("lbl_EMPBE_Step") }}
                  </h6>
                  <ol>
                    <li
                      v-for="(msg, index) in messages('instruction_')"
                      :key="index"
                    >
                      {{ msg }}
                    </li>
                  </ol>
                </div>

                <div class="col-6">
                  <h6 class="font-weight-medium">
                    {{ $t("lbl_EMPBE_Reminder") }}
                  </h6>
                  <ol>
                    <li
                      v-for="(msg, index) in messages('reminder_')"
                      :key="index"
                    >
                      {{ msg }}
                    </li>
                  </ol>
                </div>
              </div>
            </custom-collapse>

            <div class="row m-t-30">
              <div class="col-3">
                <b-button variant="success" @click="onDownloadTemplate">
                  <i
                    class="icofont icofont-download-alt"
                    style="line-height: 1.5"
                  />
                  {{ this.$t("lbl_EMPBE_DownloadTemplate") }}
                </b-button>
              </div>

              <div class="col-9">
                <b-form-file
                  id="uploadExcelFile"
                  ref="uploadExcelFile"
                  class="flex-fill"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  :placeholder="$t('lbl_EMPBE_InputFile_Placeholder')"
                  v-model="uploadExcelFile"
                  :browse-text="$t('lbl_EMPBE_InputFile_BrowseText')"
                  :no-drop-placeholder="
                    $t('lbl_EMPBE_InputFile_NoDropPlaceholder')
                  "
                  :drop-placeholder="$t('lbl_EMPBE_InputFile_DropPlaceholder')"
                  :state="uploadExcelFileErrorMsg ? false : null"
                  @change="onChangeFile"
                />

                <b-form-invalid-feedback
                  :state="uploadExcelFileErrorMsg ? false : null"
                >
                  {{ uploadExcelFileErrorMsg }}
                </b-form-invalid-feedback>
              </div>
            </div>

            <b-table-simple
              v-if="errorDataInFile.length > 0"
              class="m-t-20"
              hover
              small
              responsive
            >
              <b-thead>
                <b-tr>
                  <b-th>query</b-th>
                  <b-th>result</b-th>
                  <b-th>command</b-th>
                  <b-th>error</b-th>
                </b-tr>
              </b-thead>

              <b-tbody>
                <b-tr v-for="(item, index) in errorDataInFile" :key="index">
                  <b-td
                    class="align-middle"
                    :variant="item.error.isKeyInvalid ? 'danger' : ''"
                  >
                    {{ item.query }}
                  </b-td>
                  <b-td
                    class="align-middle"
                    :variant="item.error.isValueInvalid ? 'danger' : ''"
                  >
                    {{ item.result }}
                  </b-td>
                  <b-td
                    class="align-middle"
                    :variant="item.error.isInvalidCommand ? 'danger' : ''"
                  >
                    {{ item.command }}
                  </b-td>
                  <b-td>
                    <p
                      v-for="(msg, index) in item.error.errorMessages"
                      :key="index"
                      class="m-b-0"
                    >
                      {{ `${index + 1}. ` + msg }}
                    </p>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </div>
      </div>
      <div
        class="card-footer customStickyCardFooter d-flex justify-content-end"
      >
        <b-button-group>
          <b-button
            v-if="isNewForm"
            variant="primary"
            size="lg"
            @click="onClickSave"
          >
            <i class="fa fa-save m-r-5" />{{ this.$t("lbl_EMPBE_Create") }}
          </b-button>

          <b-button
            v-if="isEditForm"
            variant="success"
            size="lg"
            :disabled="form.value === initForm.value"
            @click="onClickUpdate"
          >
            <i class="fa fa-save m-r-5" />{{ this.$t("lbl_EMPBE_Update") }}
          </b-button>

          <b-button
            v-if="isBatchForm"
            variant="info"
            size="lg"
            @click="onClickUpload"
          >
            <i class="icofont icofont-upload-alt m-r-5" />{{
              this.$t("lbl_EMPBE_Upload")
            }}
          </b-button>
        </b-button-group>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import { read as xlsxRead, utils as xlsxUtils } from "xlsx";
import { exportDataToExcel } from "@/_helpers/excel";

export default {
  props: ["initForm"],

  data() {
    return {
      isLoading: false,
      form: {
        // command: "add", // "add" || "edit" || "batch"
        // key: "",
        // value: "",
        // created: "",
        // updated: "",
      },
      formErrorMsg: {
        key: "",
        value: "",
      },
      uploadExcelFile: null,
      uploadExcelFileErrorMsg: "",
      errorDataInFile: [],
    };
  },

  computed: {
    langid() {
      return localStorage.getItem("currentLangId");
    },

    isAdmin() {
      return this.user.userType == "ADMIN";
    },

    isNewForm() {
      return this.form.command?.toLowerCase() === "add";
    },

    isEditForm() {
      return this.form.command?.toLowerCase() === "edit";
    },

    isBatchForm() {
      return this.form.command?.toLowerCase() === "batch";
    },

    title() {
      const titleMap = {
        add: this.$t("lbl_EMPBE_Add"),
        edit: this.$t("lbl_EMPBE_Edit"),
        batch: this.$t("lbl_EMPBE_BatchAction"),
      };

      return this.form.command ? titleMap[this.form.command] : "";
    },

    localeMap() {
      const key =
        this.langid === "1252" ? "EN" : this.langid === "950" ? "TC" : "SC";

      return {
        EN: {
          spaceInQueryError:
            "'query' cannot contain spaces, please remove spaces.",
          diffValueError:
            "Word Spliting 'result' should be same as 'query' after removing in between spaces.",
          uploadExcelError: "Please upload Excel file.",
          excelNoDataError: "Excel file must contain data.",
          exccedMaxDataError:
            "Excel file cannot contain more than 1000 records.",
          excelDataInvalidError: "Excel file contains invalid data.",
          commandIsNotValid: "'command' must be 'add' or 'delete'.",
          dulplicateKeyError:
            "More than 1 record of same 'query' value is found.",
          keyLengthExceedMaxError:
            "'query' value length cannot be greater than 10.",
          valueLengthExceedMaxError:
            "'value' value length cannot be greater than 32.",
          reminder_1: "'query' is the search word of users.",
          reminder_2:
            "'query' value length with more than 10 will be filter out.",
          reminder_3:
            "'result' is the splited word restricted by admin to be use in searching.",
          reminder_4:
            "'result' value length with more than 32 will be filter out.",
          reminder_5: "'command' can be 'add' or 'delete' only.",
          reminder_6: "'add' will be used to add new word.",
          reminder_7: "'delete' will be used to delete word.",
          reminder_8:
            "If you would like to update a word and it is already in the list, please use 'add' in 'command', and the 'result' of the word will be overwritten.",
          instruction_1: "Download template.",
          instruction_2: "Input valid data and save it.",
          instruction_3:
            "Select the file in input and click 'Upload' button. (If a table of error is shown, please correct it in excel file and upload again)",
          example_1:
            "If you would like to split the word, use space bar to separate it, e.g. query: test -> result: te st",
          example_2:
            "If you would like to restrict it to be one word, then 'query' should be same as 'result', e.g. query: test -> result: test",
          detail_1: "'Query' is the search word of users.",
          detail_2:
            "'Word Spliting Result' is the splited word restricted by admin to be use in searching.",
        },
        TC: {
          spaceInQueryError: "'搜尋字串' 不能包含空格，請刪除空格。",
          diffValueError: "刪除空格後的 '分詞結果' 應與 '搜尋字串' 相同。",
          uploadExcelError: "請上傳 Excel 檔案。",
          excelNoDataError: "Excel 檔案必須包含資料。",
          exccedMaxDataError: "Excel 檔案不能包含超過 1000 筆記錄。",
          excelDataInvalidError: "Excel 檔案包含無效資料。",
          commandIsNotValid: "'command' 必須是 'add' 或 'delete'。",
          dulplicateKeyError: "有重複的 '搜尋字串' 值。",
          keyLengthExceedMaxError: "'搜尋字串' 值長度不能大於 10。",
          valueLengthExceedMaxError: "'分詞結果' 值長度不能大於 32。",
          reminder_1: "'query' 是用於搜尋的關鍵字。",
          reminder_2: "'query' 值長度超過 10 則會被過濾掉。",
          reminder_3: "'result' 是用於管理員將搜尋關鍵字分割。",
          reminder_4: "'result' 值長度超過 32 則會被過濾掉。",
          reminder_5: "'command' 只能是 'add' 或 'delete'。",
          reminder_6: "'add' 將用於新增新的關鍵字。",
          reminder_7: "'delete' 將用於刪除關鍵字。",
          reminder_8:
            "如果您想更新一個已存在的關鍵字，請在 'command' 中使用 'add' ，該關鍵字中的 'result' 將會被覆蓋。",
          instruction_1: "下載範本。",
          instruction_2: "輸入有效資料並保存它。",
          instruction_3:
            "選擇檔案並點擊 '上載' 按鈕。 (如果顯示錯誤表格，請在 Excel 檔案中修正並重新上傳)",
          example_1:
            "如果您想分割字串，請使用空格區分它，例如，搜尋字串: test -> 分詞結果: te st",
          example_2:
            "如果您想限制搜尋字串為同一個字詞，則 '搜尋字串' 和 '分詞結果' 的值要相同，例如，搜尋字串: test -> 分詞結果: test",
          detail_1: "'搜尋字串' 是用於搜尋的關鍵字。",
          detail_2: "'分詞結果' 是用於管理員將搜尋關鍵字分割。",
        },
        SC: {
          spaceInQueryError: "'搜索字串' 不能包含空格，请删除空格。",
          diffValueError: "删除空格后的 '分词结果' 应与 '搜索字串' 相同",
          uploadExcelError: "请上传 Excel 文件。",
          excelNoDataError: "Excel 文件必须包含数据。",
          exccedMaxDataError: "Excel 文件不能包含超过 1000 条记录。",
          excelDataInvalidError: "Excel 文件包含无效数据。",
          commandIsNotValid: "'command' 必须是 'add' 或 'delete'",
          dulplicateKeyError: "有重复的 'query' 值",
          keyLengthExceedMaxError: "'搜索字串' 值長度不能大於 10。",
          valueLengthExceedMaxError: "'分词结果' 值長度不能大於 32。",
          reminder_1: "'query' 是用于搜索的关键字。",
          reminder_2: "'query' 值长度超过 10 时将会被过滤掉。",
          reminder_3: "'result' 是用于管理员将搜索关键字分割。",
          reminder_4: "'result' 值长度超过 32 时将会被过滤掉。",
          reminder_5: "'command' 只能是 'add' 或 'delete'。",
          reminder_6: "'add' 将用于新增新的关键字。",
          reminder_7: "'delete' 将用于删除关键字。",
          reminder_8:
            "如果您想更新一条已存在的关键字，请在 'command' 中使用 'add' ，该关键字中的 'result' 将会被覆盖。",
          instruction_1: "下载模板。",
          instruction_2: "输入有效数据并保存它。",
          instruction_3:
            "选择文件并点击 '上传' 按钮。 (如果显示错误表格，请在 Excel 文件中修正并重新上传)",
          example_1:
            "如果您想分割字符串，请使用空格分隔它，例如，搜索字串: test -> 分词结果: te st",
          example_2:
            "如果您想限制搜索字符串为同一个词，则 '搜索字串' 和 '分词结果' 的值应相同，例如，搜索字串: test -> 分词结果: test",
          detail_1: "'搜索字串' 是用于搜索的关键字。",
          detail_2: "'分词结果' 是用于管理员将搜索关键字分割。",
        },
      }[key];
    },

    messages() {
      return (prefix) => {
        const localeMap = this.localeMap;

        return Object.keys(localeMap)
          .filter((key) => key.includes(prefix))
          .map((_, index) => {
            const num = index + 1;
            const localeMapKey = prefix + num.toString();
            return localeMap[localeMapKey];
          });
      };
    },

    diffValueError() {
      return this.localeMap.diffValueError;
    },

    commandIsNotValidError() {
      return this.localeMap.commandIsNotValid;
    },

    dulplicateKeyError() {
      return this.localeMap.dulplicateKeyError;
    },
  },

  created() {
    this.isLoading = false; // Used in parent component
    this.formErrorMsg = {
      key: "",
      value: "",
    };
    this.uploadExcelFile = null;
    this.uploadExcelFileErrorMsg = "";
    this.errorDataInFile = [];

    this.form = {
      ...this.initForm,
    };
  },

  methods: {
    async onClickSave() {
      const isFormValid = await this.validateForm();

      if (!isFormValid) return;

      const formatedForm = this.formatData({ ...this.form, command: "add" }); // Force command to be "add"

      this.$emit("onClickSave", formatedForm);
    },

    async onClickUpdate() {
      const isFormValid = await this.validateForm();

      const isDirty = this.form.value !== this.initForm.value;

      if (!isFormValid || !isDirty) return;

      const formatedForm = this.formatData({ ...this.form, command: "add" }); // Force command to be "add"

      this.$emit("onClickUpdate", formatedForm);
    },

    async validateForm() {
      const isKeyValid = await this.validateFormKeyData(
        this.form.key,
        true
      ).catch((err) => {
        this.formErrorMsg.key = err;
      });

      const isValueValid = await this.validateFormValueData(
        this.form.key,
        this.form.value,
        true
      ).catch((err) => {
        this.formErrorMsg.value = err;
      });

      return isKeyValid && isValueValid;
    },

    /**
     * Validates the form key data.
     *
     * @param {string} keyData - The key data to be validated.
     * @param {boolean} [isSingleAction=false] - Indicates whether the validation is for a single action.
     * @return {Promise<boolean>} A promise that resolves to true if the form key data is valid, or rejects with an error message if it is not.
     */
    async validateFormKeyData(keyData, isSingleAction = false) {
      return new Promise((resolve, reject) => {
        if (!keyData) {
          const msg =
            this.$t("lbl_EMPBE_OpenSearch_Key") + this.$t("lbl_IsRequire");

          reject(msg);
        }

        if (isSingleAction && keyData.length > 10) {
          reject(this.localeMap.keyLengthExceedMaxError);
        }

        if (keyData.includes(" ")) {
          reject(this.localeMap.spaceInQueryError);
        }

        resolve(true);
      });
    },

    /**
     * Validates the form value data.
     *
     * @param {string} keyData - The key data to be validated.
     * @param {string} valueData - The value data to be validated.
     * @param {boolean} [isSingleAction=false] - Indicates whether the validation is for a single action.
     * @return {Promise<boolean>} A promise that resolves to true if the form value data is valid, or rejects with an error message if it is not.
     */
    async validateFormValueData(keyData, valueData, isSingleAction = false) {
      return new Promise((resolve, reject) => {
        if (!valueData) {
          const msg =
            this.$t("lbl_EMPBE_OpenSearch_Value") + this.$t("lbl_IsRequire");

          reject(msg);
        }

        if (isSingleAction && valueData.length > 32) {
          reject(this.localeMap.valueLengthExceedMaxError);
        }

        const isDiff = this.isKeyAndValueDiff(keyData, valueData);

        if (isDiff) {
          reject(this.localeMap.diffValueError);
        }

        resolve(true);
      });
    },

    /**
     * Validates the uploaded file.
     *
     * @return {Promise<boolean>} A promise that resolves to true if the file is valid,
     *                           or rejects with an error message if the file is invalid.
     */
    async validateFile() {
      return new Promise(async (resolve, reject) => {
        const uploadFile = this.uploadExcelFile;
        if (!uploadFile) {
          const msg = this.$t("lbl_EMPBE_PleaseUploadFile");

          reject(msg);
        }

        const hasExcelExtension =
          uploadFile.name.endsWith(".xlsx") || uploadFile.name.endsWith(".xls");

        if (!hasExcelExtension) {
          reject(this.localeMap.uploadExcelError);
        }

        resolve(true);
      });
    },

    /**
     * Returns the validation state of the form based on the provided key.
     *
     * @param {"key" | "value"} isFormValidKey - The key to retrieve the validation state from the `formErrorMsg` object.
     * @return {"null" | "false"} The validation state of the form for the provided key, or `null` if the key is not found.
     */
    getValidationState(isFormValidKey) {
      return this.formErrorMsg[isFormValidKey] ? false : null;
    },

    resetUploadFileError() {
      this.uploadExcelFileErrorMsg = "";
      this.errorDataInFile = [];
    },

    async onClickUpload() {
      this.resetUploadFileError();

      const isFileValid = await this.validateFile().catch((err) => {
        this.uploadExcelFileErrorMsg = err;
      });

      if (!isFileValid) return;

      const listOfForms = await this.excelFileToListObject(
        this.uploadExcelFile
      ).catch((err) => {
        this.uploadExcelFileErrorMsg = err;
        return;
      });

      if (!listOfForms) {
        this.uploadExcelFileErrorMsg = this.localeMap.uploadExcelError;
        return;
      }

      if (!listOfForms.length) {
        this.uploadExcelFileErrorMsg = this.localeMap.excelNoDataError;
        return;
      }

      if (listOfForms.length > 1000) {
        this.uploadExcelFileErrorMsg = this.localeMap.exccedMaxDataError;
        return;
      }

      const validData = await this.validateAndMapAllDataInFile(
        listOfForms
      ).catch((err) => {
        this.uploadExcelFileErrorMsg = err;
        return;
      });

      if (!validData) return;

      this.$emit("onClickUpload", validData);
    },

    /**
     * Validates and maps all data in the file.
     *
     * @param {{query: string; result: string; command: string}[]} data - The data to be validated and mapped.
     * @return {Promise<{key: string; value: string; command: string}[]>} A promise that resolves to the mapped data if successful, or rejects with an error message.
     */
    async validateAndMapAllDataInFile(data) {
      return new Promise(async (resolve, reject) => {
        try {
          const mappedData = new Map(); // Use for checking dulplicate keys
          let isError = false;

          for (const item of data) {
            const formatedData = this.formatData({
              key: item.query,
              value: item.result,
              command: item.command,
            });

            const formatedDataKey = formatedData.key;
            const formatedDataValue = formatedData.value;
            const isKeyDulplicate = mappedData.has(formatedDataKey);

            const isCommandAddOrDelete =
              item.command &&
              (item.command.toLowerCase() === "add" ||
                item.command.toLowerCase() === "delete");

            const valueErrorMessage = await this.validateFormValueData(
              formatedDataKey,
              formatedDataValue
            )
              .then((_) => "")
              .catch((err) => err);

            const keyErrorMessage = await this.validateFormKeyData(
              formatedDataKey
            )
              .then((_) => "")
              .catch((err) => err);

            if (
              isKeyDulplicate ||
              !isCommandAddOrDelete ||
              valueErrorMessage ||
              keyErrorMessage
            ) {
              isError = true;

              const errorMessages = [
                keyErrorMessage,
                isKeyDulplicate ? this.dulplicateKeyError : "",
                valueErrorMessage,
                !isCommandAddOrDelete ? this.commandIsNotValidError : "",
              ].filter((item) => !!item);

              this.errorDataInFile.push({
                ...item,
                isValid: false,
                error: {
                  isKeyInvalid: !!keyErrorMessage || isKeyDulplicate,
                  isValueInvalid: !!valueErrorMessage,
                  isInvalidCommand: !isCommandAddOrDelete,

                  errorMessages,
                },
              });
            }

            // No matter the mappedData has error data or not, if the mappedData has error data, the promise will be rejected
            mappedData.set(formatedDataKey, formatedData);
          }

          // If there is an error in the mappedData, the promise will be rejected
          if (isError) {
            reject(this.localeMap.excelDataInvalidError);
          }

          const response = Array.from(mappedData.entries(), ([key, value]) => ({
            ...value,
            key, // Cover the key inside the value object
          }));

          resolve(response);
        } catch (err) {
          reject(err?.message || `${err}`);
        }
      });
    },

    excelFileToListObject(file) {
      return new Promise((resolve, reject) => {
        try {
          if (!file) {
            reject(this.localeMap.uploadExcelError);
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            /* Parse data */
            const bstr = e.target.result;
            const wb = xlsxRead(bstr, { type: "binary" });
            /* Get first worksheet */
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            /* Convert array of arrays */
            const data = xlsxUtils.sheet_to_json(ws);

            resolve(data);
          };

          reader.readAsArrayBuffer(file);
        } catch (err) {
          reject(err?.message || `${err}`);
        }
      });
    },

    onHideForm() {
      this.$emit("onHideForm");
    },

    onChangeFile(event) {
      this.resetUploadFileError();
    },

    onDownloadTemplate() {
      const schema = [["query", "result", "command"]];

      const params = {
        data: schema,
        fileName: "OpenSearch_WordSplit_Template",
        isDataTypeAoa: true,
        writeFileOptions: { compression: true },
      };

      exportDataToExcel(params);
    },

    /**
     * A function that formats the data from the form input.
     *
     * @param {{key: string; value: string; command: string}} form - The form object containing key, value, and command properties.
     * @return {{key: string; value: string; cmd: string}} Returns an object with formatted key, value, and command properties.
     */
    formatData(form) {
      return {
        key: form.key.trim(),
        value: form.value.trim(),
        cmd: form.command.trim().toLowerCase(),
      };
    },

    /**
     * Checks if the key and value are different after removing spaces.
     *
     * @param {string} key - The key to compare.
     * @param {string} value - The value to compare.
     * @return {boolean} Returns true if the key and value are different after removing spaces, otherwise returns false.
     */
    isKeyAndValueDiff(key, value) {
      const joinedValue = value.split(" ").join("");

      if (joinedValue !== key) return true;

      return false;
    },
  },
};
</script>

<style lang="scss">
input[type="text"].form-control:disabled {
  background-color: #f9f9f9 !important;
}
</style>
